import { useQuery } from '@hooks/reactQuery';

import { getGoldenSettingsInfo } from '@api/goldenSettings';

import { extractQueryParams } from '@utils/common';
import { parseStringDateStartOfTheDay } from '@utils/date';
import { handleErrorFromMessage } from '@utils/handleRequestError';

import { TGoldenSettingsOptionDTO } from '@interfaces/cmModule/dto/goldenSettingsDTO';
import { IGoldenSettingsInfo } from '@interfaces/cmModule/goldenSettings';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { EVendorsValues } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import { refetchOptions } from '@constants/common';
import { GOLDEN_SETTING_INFO } from '@constants/queryKeys';

interface IUseQueryGoldenSettingsInfoReturnType {
	goldenSettingsInfo?: IGoldenSettingsInfo;
	isLoadingGoldenSettingsInfo: boolean;
}

const useQueryGoldenSettingsInfo = (
	params: TGoldenSettingsOptionDTO,
): IUseQueryGoldenSettingsInfoReturnType => {
	const {
		vendor,
		technology,
		nodeType,
		nodes,
		type,
		version,
	} = params || {};

	const isEnabled = vendor === EVendorsValues.mavenir
		? !!vendor && !!nodes && !!type && !!version
		: !!vendor && !!technology && !!nodeType;

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [
			GOLDEN_SETTING_INFO,
			params,
		],
		queryFn: extractQueryParams(getGoldenSettingsInfo),
		select: (data) => {
			const parsedDateText = data.dateText ? parseStringDateStartOfTheDay(data.dateText) : null;

			return {
				...data,
				date: data.date ? data.date : parsedDateText,
			};
		},
		onError: (err: IResponseError) => {
			if (!err.errors.find((item) => item.code === EErrorCodes.goldenSettingsNotSet)) {
				handleErrorFromMessage(err);
			}
		},
		enabled: [isEnabled],
		...refetchOptions,
	});

	return {
		goldenSettingsInfo: data,
		isLoadingGoldenSettingsInfo: (isLoading && !!data) || isFetching,
	};
};

export default useQueryGoldenSettingsInfo;
